import React, { useEffect, useState } from "react";
import { CalendarOutlined, PlusCircleOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Dropdown,
  Empty,
  Form,
  Input,
  Modal,
  Pagination,
  Row,
  Space,
  Typography,
} from "antd";
import { Link, Outlet } from "react-router-dom";
import dayjs from "dayjs";
import MoreIcon from "../../assets/images/menu-icon.svg";
import { useAxios } from "../../hooks/useAxios";
import { useNotification } from "../../contexts/NotificationProvider";
import SearchInput from "../FormFields/SearchInput";
import { useScreenSize } from "../../hooks/useScreenSize";

const { Text } = Typography;

const RotasView = () => {
  const { axiosInstance } = useAxios();
  const [weeks, setWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedWeekId, setSelectedWeekId] = useState(null);
  const [weekRange, setWeekRange] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const notify = useNotification();
  const [form] = Form.useForm();
  const [currentPage, setCurrentPage] = useState(1); // Add currentPage state

  const isLargeScreen = useScreenSize(); // Custom hook to check screen size
  const pageSize = isLargeScreen ? 9 : 6; // Set page size based on screen size
  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
    setSelectedWeek(null);
    setSelectedWeekId(null);
    setWeekRange(null);
  };
  const createWeek = async (formData) => {
    setLoading(true);
    const { title, start_date } = formData;

    if (start_date && start_date.isValid()) {
      let startOfWeek = start_date.startOf("week");
      if (startOfWeek.day() !== 1) {
        startOfWeek = startOfWeek.add(1, "day");
      }
      const formattedStartDate = startOfWeek.format("YYYY-MM-DD");

      const requestBody = {
        title,
        start_date: formattedStartDate,
      };
      try {
        const response =
          selectedWeekId === null
            ? await axiosInstance.post("/api/weeks/create/", requestBody)
            : await axiosInstance.put(
                `/api/weeks/${selectedWeekId}/`,
                requestBody
              );
        selectedWeekId === null
          ? setWeeks((prevState) => {
              return [response.data, ...prevState];
            })
          : setWeeks((prevState) => {
              const weekIndex = prevState.findIndex(
                (week) => week.id === selectedWeekId
              );
              if (weekIndex !== -1) {
                prevState[weekIndex] = response.data;
              }
              return prevState;
            });
        notify({
          type: "success",
          message: `Week ${
            selectedWeekId ? "updated" : "created"
          } successfully`,
        });
      } catch (e) {
        console.log("Error creating employee", e);
      } finally {
        setLoading(false);
        setIsModalOpen(false);
        form.resetFields();
        setWeekRange(null);
      }
    }
  };
  const confirmDelete = async (id) => {
    setLoading(true);
    try {
      await axiosInstance.delete(`/api/weeks/${id}/`);
      await fetchWeeks();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      setIsModalOpen(false);
      form.resetFields();
      setWeekRange(null);
      setSelectedWeekId(null);
      setSelectedWeek(null);
    }
  };
  const fetchWeeks = async (searchTerm = "") => {
    setLoading(true);
    try {
      const params = searchTerm ? { search: searchTerm } : {};
      const response = await axiosInstance.get("/api/weeks/list/", {params});
      setWeeks(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchWeeks();
    setHasSearched(false);
    //eslint-disable-next-line
  }, []);
  useEffect(() => {
    if (selectedWeek && selectedWeek !== null) {
      form.setFieldValue("title", selectedWeek?.title);
      form.setFieldValue("start_date", dayjs(selectedWeek?.start_date));
      setIsModalOpen(true);
    } else {
      form.setFields([
        {
          name: "title",
          value: null,
        },
        {
          name: "start_date",
          value: null,
        },
      ]);
    }
  }, [selectedWeek, form]);
  const handleSearch = async (searchTerm)=>{
    setHasSearched(true);
    await fetchWeeks(searchTerm);
  }

  const getMenu = (id) => {
    const items = [
      {
        label: <Text>Edit Week</Text>,
        key: "0",
        meta: {
          type: "edit",
        },
      },
      {
        label: <Text type="danger">Delete</Text>,
        key: "1",
        meta: {
          type: "delete",
        },
      },
    ];
    const onClick = (e) => {
      const itemMeta = e.item.props.meta;
      const currentWeek = weeks.find((w) => w.id === id);
      if (itemMeta.type === "edit") {
        setSelectedWeek({
          title: currentWeek?.title,
          start_date: dayjs(currentWeek.start_date),
        });
      }
      if (itemMeta.type === "delete") {
        Modal.confirm({
          title: "Are you sure you want to delete?",
          content:
            "Deleting this week will remove the rota associated with this.",
          onOk: () => confirmDelete(id),
          onCancel: () => handleCancel(),
        });
      }
      setSelectedWeekId(id);
    };
    return {
      items,
      onClick,
    };
  };
  const formatWeekNo = (weekNo) => {
    if (!weekNo) return "";

    const weekNumber = parseInt(weekNo.split("-")[1], 10); // Extract the week number as an integer

    // Determine the correct suffix
    const suffix = (n) => {
      if (n > 10 && n < 20) return "th"; // Special case for 11th, 12th, 13th, etc.
      switch (n % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    };

    return `${weekNumber}${suffix(weekNumber)}`;
  };
  const handleWeekChange = (date) => {
    if (date) {
      const startOfWeek = date.startOf("week").add(1, "day"); // Monday
      const endOfWeek = startOfWeek.add(6, "day"); // Sunday
      setWeekRange(
        `Start Date: ${startOfWeek.format(
          "YYYY-MM-DD"
        )}, End Date: ${endOfWeek.format("YYYY-MM-DD")}`
      );
    } else {
      setWeekRange(null);
    }
  };
  useEffect(() => {
    if (selectedWeek && selectedWeek !== null) {
      form.setFieldValue("title", selectedWeek?.title);
      const startDate = dayjs(selectedWeek?.start_date)
        .startOf("week")
        .add(1, "day"); // Start of the week (Monday)
      const endDate = startDate.add(6, "day"); // End of the week (Sunday)
      setWeekRange(
        `Start Date: ${startDate.format(
          "YYYY-MM-DD"
        )}, End Date: ${endDate.format("YYYY-MM-DD")}`
      );
      form.setFieldValue("start_date", dayjs(selectedWeek?.start_date));
      setIsModalOpen(true);
    } else {
      form.setFields([
        {
          name: "title",
          value: null,
        },
        {
          name: "start_date",
          value: null,
        },
      ]);
      setWeekRange(null); // Clear the hint if no week is selected
    }
  }, [selectedWeek, form]);
  const disableFutureWeeks = (current) => {
    return current && current.isAfter(dayjs(), "week");
  };

  return (
    <>
      <Outlet />
      <Modal
        title={selectedWeekId ? "Edit Week" : "Create Week "}
        open={isModalOpen}
        onCancel={handleCancel}
        onClose={handleCancel}
        wrapClassName="main-modal-class notification-modal"
        footer={[
          <Button
            style={{
              backgroundColor: "transparent",
              color: "#324054",
              border: "none",
              borderRadius: "4px",
              padding: "0 16px",
              height: "40px",
              fontWeight: 600,
              width: "118px",
            }}
            variant="text"
            onClick={handleCancel}
          >
            Cancel
          </Button>,
          <Button
            style={{
              backgroundColor: "#212180",
              color: "#FFF",
              border: "none",
              borderRadius: "4px",
              padding: "0 16px",
              height: "40px",
              fontWeight: 600,
              width: "118px",
            }}
            variant="text"
            htmlType="submit"
            loading={loading}
            disabled={loading}
            onClick={() => form.submit()}
          >
            {selectedWeekId ? "Update" : "Create"}
          </Button>,
        ]}
        className="upload-csv-modal"
      >
        <Form
          form={form}
          layout="vertical"
          name="employeeForm"
          requiredMark={false}
          onFinish={createWeek}
          initialValues={selectedWeek}
        >
          <Form.Item
            name="title"
            label="Title"
            rules={[
              { required: true, message: "Title is required" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const getWeek = weeks.find((i) => i.id === selectedWeekId);
                  if (
                    value.trim().toLowerCase() ===
                    getWeek?.title?.trim().toLowerCase()
                  ) {
                    return Promise.reject(
                      new Error("Title is same as current title.")
                    );
                  }
                  if (value && value.trim()) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("Title cannot be empty or just spaces")
                  );
                },
              }),
            ]}
          >
            <Input placeholder="Week title" />
          </Form.Item>
          <Form.Item
            name="start_date"
            label="Select Week"
            rules={[
              {
                required: true,
                message: "Start date is required",
              },
            ]}
            help={weekRange || "Select a week to see its start and end dates."}
          >
            <DatePicker
              picker="week"
              format="YYYY-ww"
              style={{ width: "100%" }}
              onChange={handleWeekChange}
              disabled={selectedWeekId !== null}
              // disabledDate={disableFutureWeeks} 
            />
          </Form.Item>
        </Form>
      </Modal>
      {
        (weeks.length > 0 || hasSearched) ? (
          <div
            style={{
              marginBottom: 14,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Col xs={24} sm={24} md={12} lg={6}>
              <SearchInput
                onSearch={handleSearch}
                placeholder="Search by name..."
                style={{ width: "100%" }}
              />
            </Col>
            <Button
              style={{
                background: "#212180",
                color: "#fff",
                width: "190px",
                height: "40px",
                padding: "12px 15px",
              }}
              onClick={showModal}
              icon={<PlusCircleOutlined />}
            >
              Add Week
            </Button>
          </div>
        ) : null
    }
      {weeks.length ? (
        <>
          <Row className="weeks-row" gutter={[24, 24]}>
            {weeks
              .slice((currentPage - 1) * pageSize, currentPage * pageSize)
              .map((week, index) => {
                return (
                  <Col span={24} md={12} lg={8} key={index}>
                    <Card
                      title={week?.title}
                      bordered={false}
                      extra={
                        <Dropdown menu={getMenu(week?.id)} trigger={["click"]}>
                          <img
                            src={MoreIcon}
                            alt="menu_icon"
                            style={{ cursor: "pointer" }}
                          />
                        </Dropdown>
                      }
                    >
                      <Space>
                        <CalendarOutlined color="#292D32" fill="#292D32" />{" "}
                        <Text type="secondary">
                          Start Date: {week.start_date}
                        </Text>
                      </Space>
                      <Space>
                        <CalendarOutlined color="#292D32" fill="#292D32" />{" "}
                        <Text type="secondary">End Date: {week.end_date}</Text>
                      </Space>
                      <Link
                        to={`/rotas/${week?.id}`}
                        style={{
                          width: "100%",
                          padding: "8px 10px 8px 10px",
                          borderRadius: "6px",
                          backgroundColor: "#F0F1F7",
                          color: "#212180",
                          fontSize: "12px",
                          fontWeight: "500",
                          lineHeight: "11.43px",
                          textAlign: "center",
                          marginTop: "20px",
                          marginBottom: "-8px",
                          zIndex: 2,
                        }}
                      >
                        View Week
                      </Link>

                      <div
                        style={{
                          position: "absolute",
                          bottom: "13px",
                          right: "13px",
                          width: "100px",
                          height: "100px",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="#21218030"
                          transform="scale(4.2,4.2)"
                        >
                          <path d="M19 3h-1v-2h-2v2h-6v-2h-2v2h-1c-1.104 0-2 .896-2 2v14c0 1.104.896 2 2 2h14c1.104 0 2-.896 2-2v-14c0-1.104-.896-2-2-2zm0 16h-14v-10h14v10z" />
                        </svg>
                        <span
                          style={{
                            fontSize: "24px",
                            fontWeight: "600",
                            color: "#2E388D30", // Same color as icon for consistency
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                          }}
                        >
                          {formatWeekNo(week?.week_of_year)}
                        </span>
                      </div>
                    </Card>
                  </Col>
                );
              })}
          </Row>
          <div style={{
            position: "absolute",
            bottom: "0%",
            right: "0%",
            background: "#f5f5f5",
           }}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={weeks.length}
              onChange={(page) => setCurrentPage(page)}
              className="custom-pagination"
            />
          </div>
        </>
      ) : (
        <Empty
          description={
            <Space direction="vertical">
              <Typography.Text>
                No weeks found. Please click the button below to add data.
              </Typography.Text>
              <Button
                type="default"
                onClick={showModal}
                icon={<PlusCircleOutlined />}
              >
                Create Week
              </Button>
            </Space>
          }
          style={{
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            gap: "20px",
          }}
        />
      )}
    </>
  );
};

export default React.memo(RotasView);